import { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "antd";
import axios from "axios";
import { BASE_URL } from "../../Constant";
import igiLogo from "./igi-logo.png";

/**
 * Customer-facing centre-diamond chooser for engagement rings.
 *
 * A "Choose Your Diamond" button opens a wizard modal:
 *   Step 1 — Diamond type (Lab or Natural, whichever feeds are priced).
 *   Step 2 — Carat / Colour / Clarity (from the admin allow-lists).
 * On confirm, a "Your Diamond" summary with the IGI certificate shows in
 * place (above Select Quality), with an Edit option. Shape is fixed to the
 * listing's primary shape (shown before carat, not changeable); cut is
 * Excellent. Pricing comes off the live Nivoda feed (centre-stone/lab-price),
 * the lab or the natural rate card depending on the type chosen.
 *
 * Self-contained + only mounted (guarded) for productType === "engagement-ring".
 */

const GOLD = "#C5A028";
// Which Nivoda rate card each diamond type prices off.
const FEED_FOR = { lab: "labgrownWhite", natural: "naturalWhite" };
const COLOUR_SCALE = ["D", "E", "F", "G", "H", "I", "J"];
const CLARITY_SCALE = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2"];

const labelStyle = {
  fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase",
  color: "#999", marginBottom: 8,
};
const chipStyle = (on) => ({
  padding: "11px 16px", minWidth: 52, textAlign: "center", cursor: "pointer",
  border: on ? `2px solid ${GOLD}` : "1px solid #e0e0e0",
  background: on ? "rgba(197,160,40,0.06)" : "#fff",
  fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 600,
  color: on ? "#111" : "#555", borderRadius: 0, transition: "all 0.15s",
});

function IgiLogo({ height = 18 }) {
  return (
    <img src={igiLogo} alt="IGI" style={{ height, width: "auto", display: "inline-block", verticalAlign: "middle" }} />
  );
}

function OptionRow({ label, items, value, onPick }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {items.map((it) => (
          <button key={it.key} type="button" onClick={() => onPick(it.key)} style={chipStyle(value === it.key)}>
            {it.text}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EngagementCentreStone({ centreStone, onChange, initialSelection }) {
  const cs = centreStone || {};
  const shape = cs.shape || "Round";

  /* Natural is offered only when its feed has actually been ingested.
     Showing the card regardless would let a customer pick Natural and then
     be quoted nothing at all — worse than not offering it. */
  const [feeds, setFeeds] = useState({ lab: true, natural: false });
  useEffect(() => {
    let alive = true;
    axios.get(`${BASE_URL}/centre-stone/feeds-available`, { params: { shape } })
      .then((r) => { if (alive && r?.data?.type === "success") setFeeds({ lab: !!r.data.lab, natural: !!r.data.natural }); })
      .catch(() => { /* leave the default: lab only */ });
    return () => { alive = false; };
  }, [shape]);
  const carats = Array.isArray(cs.carats) ? [...cs.carats].sort((a, b) => a - b) : [];
  const colours = COLOUR_SCALE.filter((c) => (cs.colours || []).includes(c));
  const clarities = CLARITY_SCALE.filter((c) => (cs.clarities || []).includes(c));

  // The chip lists above are grade-ordered so they read sensibly in the modal, but
  // the DEFAULT comes from the order the admin saved, not from the grade scale.
  // Otherwise a listing offering D/E/F always opens on D — the dearest stone — and
  // there is no way to say "show F, but let them upgrade". The engagement builder
  // already works this way (EngagementBuilder.jsx ~L81 reads eng.colours[0]), which
  // is why Seraphina opens on F · VVS1 while still offering D and E; this keeps a
  // normal product with a centre stone consistent with it. Falls back to the
  // grade-ordered first entry when the stored order is empty.
  const defaults = {
    type: "lab",
    carat: carats[0] ?? null,
    colour: (cs.colours || [])[0] ?? colours[0] ?? null,
    clarity: (cs.clarities || [])[0] ?? clarities[0] ?? null,
  };
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);           // 0 = type, 1 = specs
  const [sel, setSel] = useState(null);          // confirmed selection
  const [draft, setDraft] = useState(defaults);  // in-modal working copy
  const [price, setPrice] = useState(null);      // { found, pricePerCaratGBP, totalGBP } — fetched to lift to the cart, not displayed

  // Live price for the in-modal draft, off the Nivoda rate card (public endpoint).
  const fetchPrice = useCallback(async (d) => {
    if (!d?.carat || !d?.colour || !d?.clarity) { setPrice(null); return; }
    try {
      const res = await axios.get(`${BASE_URL}/centre-stone/lab-price`, {
        params: { shape, carat: d.carat, colour: d.colour, clarity: d.clarity, feed: FEED_FOR[d.type] || FEED_FOR.lab },
      });
      setPrice(res?.data || null);
    } catch (_) {
      setPrice(null);
    }
  }, [shape]);

  useEffect(() => {
    if (open && step === 1) fetchPrice(draft);
  }, [open, step, draft.carat, draft.colour, draft.clarity, fetchPrice]);

  // Pre-select a default diamond (e.g. the cheapest) so the shown price visibly
  // includes a stone. Runs once when the default arrives; the customer can then
  // Change it or clear it (×). Opt-in via the initialSelection prop.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current || sel || !initialSelection?.carat) return;
    didInit.current = true;
    const chosen = {
      type: "lab", carat: initialSelection.carat, colour: initialSelection.colour, clarity: initialSelection.clarity, shape,
      pricePerCaratGBP: initialSelection.pricePerCaratGBP || 0,
      totalGBP: initialSelection.totalGBP || 0,
      found: initialSelection.found !== false,
      label: [initialSelection.carat ? `${initialSelection.carat}ct` : null, initialSelection.colour, initialSelection.clarity].filter(Boolean).join(" "),
    };
    setSel({ type: "lab", carat: initialSelection.carat, colour: initialSelection.colour, clarity: initialSelection.clarity, totalGBP: chosen.totalGBP, found: chosen.found });
    onChange?.(chosen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelection]);

  if (!cs.enabled || (!carats.length && !colours.length && !clarities.length)) return null;

  const openModal = () => { setDraft(sel || defaults); setStep(sel ? 1 : 0); setOpen(true); };
  const confirm = () => {
    const chosen = {
      type: draft.type, carat: draft.carat, colour: draft.colour, clarity: draft.clarity, shape,
      pricePerCaratGBP: price?.pricePerCaratGBP || 0,
      totalGBP: price?.totalGBP || 0,
      found: !!price?.found,
      label: [draft.carat ? `${draft.carat}ct` : null, draft.colour, draft.clarity].filter(Boolean).join(" "),
    };
    setSel({ ...draft, totalGBP: chosen.totalGBP, found: chosen.found });
    setOpen(false);
    onChange?.(chosen);
  };

  const detail = sel
    ? [sel.type === "natural" ? "Natural" : "Lab Grown", shape, sel.carat ? `${sel.carat}ct` : null, sel.colour, sel.clarity, "Excellent"]
        .filter(Boolean)
        .join("  ·  ")
    : "";

  const typeCardStyle = (active, disabled) => ({
    flex: 1, minWidth: 0, padding: "22px 16px", textAlign: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    border: active ? `2px solid ${GOLD}` : "1px solid #e0e0e0",
    background: disabled ? "#f5f5f5" : active ? "rgba(197,160,40,0.06)" : "#fff",
    opacity: disabled ? 0.55 : 1, borderRadius: 0, fontFamily: "Poppins, sans-serif",
    transition: "all 0.15s",
  });

  return (
    <div>
      {!sel ? (
        <button
          type="button"
          onClick={openModal}
          style={{
            width: "100%", padding: "14px 18px", background: GOLD, color: "#fff", border: "none",
            fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 1,
            textTransform: "uppercase", cursor: "pointer", borderRadius: 0,
          }}
        >
          Choose Your Diamond
        </button>
      ) : (
        <div style={{ border: `1px solid ${GOLD}`, background: "rgba(197,160,40,0.05)", padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#111", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span>{detail}</span>
                {sel.type !== "natural" && <IgiLogo height={40} />}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 11, color: "#555" }}>
                {sel.type !== "natural" && <IgiLogo height={24} />}
                <span>{sel.type === "natural" ? "Grading certificate included" : "IGI Certificate included"}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
              <button
                type="button"
                onClick={openModal}
                style={{
                  background: "none", border: "none", color: GOLD, fontFamily: "Poppins, sans-serif",
                  fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase",
                  cursor: "pointer", padding: 0,
                }}
              >
                Change
              </button>
              <button
                type="button"
                onClick={() => { setSel(null); onChange?.(null); }}
                aria-label="Remove diamond"
                title="Remove diamond"
                style={{ background: "none", border: "none", color: "#c0392b", fontSize: 18, lineHeight: 1, cursor: "pointer", padding: 0 }}
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} title="Choose Your Diamond" width={520} destroyOnClose>
        {/* ── Step 1: Diamond type ── */}
        {step === 0 && (
          <div style={{ paddingTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>
              Step 1 of 2
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 18 }}>Choose your diamond type</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={() => { setDraft((d) => ({ ...d, type: "lab" })); setStep(1); }}
                style={typeCardStyle(draft.type === "lab", false)}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 4 }}>Lab Diamond</div>
                <div style={{ fontSize: 11, color: "#888" }}>IGI certified · {shape} · Excellent</div>
              </button>
              <button
                type="button"
                disabled={!feeds.natural}
                onClick={() => { if (!feeds.natural) return; setDraft((d) => ({ ...d, type: "natural" })); setStep(1); }}
                style={typeCardStyle(draft.type === "natural", !feeds.natural)}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 4 }}>Natural Diamond</div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  {feeds.natural ? `Certificated · ${shape} · Excellent` : "Coming soon"}
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Specification ── */}
        {step === 1 && (
          <div style={{ paddingTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>
              Step 2 of 2 — {draft.type === "natural" ? "Natural" : "Lab Grown"}
            </div>
            <div style={{ fontSize: 11, color: "#999", marginBottom: 18 }}>
              Excellent cut &middot; {draft.type === "natural" ? "Certified" : "IGI certified"}
            </div>

            {/* Shape — fixed to this listing's primary shape (shown, not changeable) */}
            <div style={{ marginBottom: 18 }}>
              <div style={labelStyle}>Shape</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <div style={{ ...chipStyle(true), cursor: "default" }} title="Set by this ring's listing">{shape}</div>
              </div>
            </div>

            {carats.length > 0 && (
              <OptionRow label="Carat" value={draft.carat} onPick={(v) => setDraft((d) => ({ ...d, carat: v }))}
                items={carats.map((c) => ({ key: c, text: `${c}ct` }))} />
            )}
            {colours.length > 0 && (
              <OptionRow label="Colour" value={draft.colour} onPick={(v) => setDraft((d) => ({ ...d, colour: v }))}
                items={colours.map((c) => ({ key: c, text: c }))} />
            )}
            {clarities.length > 0 && (
              <OptionRow label="Clarity" value={draft.clarity} onPick={(v) => setDraft((d) => ({ ...d, clarity: v }))}
                items={clarities.map((c) => ({ key: c, text: c }))} />
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#555", marginBottom: 18 }}>
              {draft.type !== "natural" && <IgiLogo height={14} />}
              <span>Every diamond ships with its {draft.type === "natural" ? "grading" : "IGI"} certificate.</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #eee", paddingTop: 16 }}>
              <button
                type="button"
                onClick={() => setStep(0)}
                style={{
                  background: "none", border: "none", color: "#888", fontFamily: "Poppins, sans-serif",
                  fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", cursor: "pointer", padding: 0,
                }}
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={confirm}
                style={{
                  padding: "11px 28px", background: GOLD, color: "#fff", border: "none",
                  fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 1,
                  textTransform: "uppercase", cursor: "pointer", borderRadius: 0,
                }}
              >
                Confirm Diamond
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
